/** Formatting helpers for numbers shown in the UI. */

/**
 * Race time as `m:ss.cc` (minutes, seconds, centiseconds), e.g. 83456 → "1:23.45".
 * Truncates instead of rounding so a time never shows a value the player did not reach.
 */
export function formatRaceTime(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 10));
  const centis = total % 100;
  const seconds = Math.floor(total / 100) % 60;
  const minutes = Math.floor(total / 6000);
  return `${minutes}:${pad2(seconds)}.${pad2(centis)}`;
}

/**
 * Time left on a clock as `m:ss`, the seconds rounded up: 5:00 at the start of a five-minute
 * race, 0:01 in its last second, 0:00 only once the time is up.
 */
export function formatTimeLeft(ms: number): string {
  const total = Math.max(0, Math.ceil(ms / 1000));
  return `${Math.floor(total / 60)}:${pad2(total % 60)}`;
}

/** Volume 0..1 as a whole percentage, e.g. 0.75 → "75%". */
export function formatPercent(value: number): string {
  return `${Math.round(Math.min(1, Math.max(0, value)) * 100)}%`;
}

/**
 * The Russian form of a noun for a count: `one` for 1, 21, 101 («уровень»), `few` for 2–4,
 * 22–24 («уровня»), `many` for 0, 5–20, 25… («уровней»).
 */
export function pluralRu(n: number, one: string, few: string, many: string): string {
  const units = Math.abs(n) % 10;
  const tens = Math.abs(n) % 100;
  if (units === 1 && tens !== 11) return one;
  if (units >= 2 && units <= 4 && (tens < 12 || tens > 14)) return few;
  return many;
}

function pad2(value: number): string {
  return value < 10 ? `0${value}` : String(value);
}
